import {
  ChartBarIcon,
  InformationCircleIcon,
  SunIcon,
} from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { Alert } from './components/alerts/Alert'
import { Grid } from './components/grid/Grid'
import { Keyboard } from './components/keyboard/Keyboard'

import { AboutModal } from './components/modals/AboutModal'
import { InfoModal } from './components/modals/InfoModal'
import { StatsModal } from './components/modals/StatsModal'
import { WordsModal } from './components/modals/WordsModal'
import {
  CORRECT_WORD_MESSAGE,
  GAME_COPIED_MESSAGE,
  NOT_ENOUGH_LETTERS_MESSAGE,
  WIN_MESSAGES,
  WORD_NOT_FOUND_MESSAGE,
  WORDLE_TITLE,
} from './constants/strings'
import {
  loadGameStateFromLocalStorage,
  loadUserFromLocalStorage,
  saveGameStateToLocalStorage,
  saveUserToLocalStorage,
} from './lib/localStorage'
import { addStatsForCompletedGame, loadStats } from './lib/stats'
import { isWinningWord, isWordInWordList, solution } from './lib/words'

import './App.css'
import { CharValues } from './lib/statuses'

const ALERT_TIME_MS = 2000

function App() {
  const prefersDarkMode = window.matchMedia(
    '(prefers-color-scheme: dark)'
  ).matches

  const [currentGuess, setCurrentGuess] = useState('')
  const [cursorPosition, setCursorPosition] = useState(0)
  const [isGameWon, setIsGameWon] = useState(false)
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false)
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false)
  const [isWordsModalOpen, setIsWordsModalOpen] = useState(false)
  const [isNotEnoughLetters, setIsNotEnoughLetters] = useState(false)
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false)
  const [isWordNotFoundAlertOpen, setIsWordNotFoundAlertOpen] = useState(false)
  const [isGameLost, setIsGameLost] = useState(false)
  const [isDarkMode, setIsDarkMode] = useState(
    localStorage.getItem('theme')
      ? localStorage.getItem('theme') === 'dark'
      : prefersDarkMode
      ? true
      : false
  )
  const [successAlert, setSuccessAlert] = useState('')
  const [guesses, setGuesses] = useState<string[]>(() => {
    const loaded = loadGameStateFromLocalStorage()
    if (loaded?.solution !== solution) {
      return []
    }
    const gameWasWon = loaded.guesses.includes(solution)
    if (gameWasWon) {
      setIsGameWon(true)
    }
    if (loaded.guesses.length === 6 && !gameWasWon) {
      setIsGameLost(true)
    }
    return loaded.guesses
  })

  // Save unique session id to local storage
  const [sessionId] = useState<string>(() => {
    const loaded = loadUserFromLocalStorage()
    if (loaded?.sessionId) {
      return loaded.sessionId
    }
    const newSessionId =
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15)
    saveUserToLocalStorage({ sessionId: newSessionId })
    return newSessionId
  })

  const [stats, setStats] = useState(() => loadStats())

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }, [isDarkMode])

  const handleDarkMode = (isDark: boolean) => {
    setIsDarkMode(isDark)
    localStorage.setItem('theme', isDark ? 'dark' : 'light')
  }

  useEffect(() => {
    saveGameStateToLocalStorage({ guesses, solution })
  }, [guesses])

  useEffect(() => {
    if (isGameWon) {
      setSuccessAlert(
        WIN_MESSAGES[Math.floor(Math.random() * WIN_MESSAGES.length)]
      )
      setTimeout(() => {
        setSuccessAlert('')
        setIsStatsModalOpen(true)
      }, ALERT_TIME_MS)
    }
    if (isGameLost) {
      setTimeout(() => {
        setIsStatsModalOpen(true)
      }, ALERT_TIME_MS)
    }
  }, [isGameWon, isGameLost])

  const onChar = (value: string) => {
    if (guesses.length < 6 && !isGameWon) {
      // Create an array representing all 5 positions
      const guessArray = Array.from({ length: 5 }, (_, i) => currentGuess[i] || ' ')
      
      // Count current non-space letters
      const currentLetterCount = guessArray.filter(char => char !== ' ').length
      
      // Check if we're overwriting an existing letter or adding a new one
      const isOverwriting = guessArray[cursorPosition] !== ' '
      
      // Only allow input if:
      // 1. We're overwriting an existing letter, OR
      // 2. We're adding a new letter and haven't reached the 5-letter limit
      if (isOverwriting || currentLetterCount < 5) {
        guessArray[cursorPosition] = value
        
        // Join back to string and trim trailing spaces
        const updatedGuess = guessArray.join('').trimEnd()
        
        setCurrentGuess(updatedGuess)
        
        // Advance cursor to next position, but don't exceed position 4
        if (cursorPosition < 4) {
          setCursorPosition(cursorPosition + 1)
        }
      }
    }
  }

  const onDelete = () => {
    if (currentGuess.length > 0) {
      // Extend string to at least cursor position for manipulation
      const paddedGuess = currentGuess.padEnd(Math.max(currentGuess.length, cursorPosition + 1), ' ')
      const guessArray = paddedGuess.split('')
      
      // Delete character at cursor position if there's something there
      if (cursorPosition < guessArray.length && guessArray[cursorPosition] !== ' ') {
        guessArray[cursorPosition] = ' '
      } else {
        // If cursor position is empty, delete the last non-space character
        for (let i = guessArray.length - 1; i >= 0; i--) {
          if (guessArray[i] !== ' ') {
            guessArray[i] = ' '
            break
          }
        }
      }
      
      // Join and trim trailing spaces
      const updatedGuess = guessArray.join('').trimEnd()
      setCurrentGuess(updatedGuess)
      
      // Move cursor back if it's beyond the new word length
      if (cursorPosition > updatedGuess.length && cursorPosition > 0) {
        setCursorPosition(Math.max(0, updatedGuess.length))
      }
    }
  }

  const onCellClick = (position: number) => {
    if (!isGameWon && !isGameLost && guesses.length < 6) {
      setCursorPosition(position)
    }
  }

  const onArrowLeft = () => {
    if (!isGameWon && !isGameLost && guesses.length < 6) {
      setCursorPosition(Math.max(0, cursorPosition - 1))
    }
  }

  const onArrowRight = () => {
    if (!isGameWon && !isGameLost && guesses.length < 6) {
      setCursorPosition(Math.min(4, cursorPosition + 1))
    }
  }

  const onEnter = () => {
    if (isGameWon || isGameLost) {
      return
    }
    if (!(currentGuess.length === 5)) {
      setIsNotEnoughLetters(true)
      return setTimeout(() => {
        setIsNotEnoughLetters(false)
      }, ALERT_TIME_MS)
    }

    if (!isWordInWordList(currentGuess)) {
      console.log('Word not found:', currentGuess, 'Showing alert')
      setIsWordNotFoundAlertOpen(true)
      return setTimeout(() => {
        setIsWordNotFoundAlertOpen(false)
      }, ALERT_TIME_MS)
    }

    const winningWord = isWinningWord(currentGuess)

    if (currentGuess.length === 5 && guesses.length < 6 && !isGameWon) {
      setGuesses([...guesses, currentGuess])
      window.flowcore.eventPoster(
        'd099ec60-79ce-46c0-b6f5-fb5003816412',
        'guess.0',
        'guess.created.0',
        {
          guess: currentGuess.toString(),
          letters: CharValues.toString(),
          guessesLeft: (6 - guesses.length).toString(),
          solution: solution.toString(),
          sessionId: sessionId,
        }
      )
      setCurrentGuess('')
      setCursorPosition(0)

      if (winningWord) {
        window.usableWebAnalytics?.('Won', {
          props: { guess: '' + (guesses.length + 1), solution: solution },
        })
        setStats(addStatsForCompletedGame(stats, guesses.length))
        return setIsGameWon(true)
      }

      if (guesses.length === 5) {
        window.usableWebAnalytics?.('Lost', {
          props: { guess: '' + (guesses.length + 1), solution: solution },
        })
        setStats(addStatsForCompletedGame(stats, guesses.length + 1))
        setIsGameLost(true)
      }
    }
  }

  return (
    <div className="pt-4 pb-2 sm:py-8 max-[375px]:pt-2 max-[375px]:pb-1 max-[350px]:pt-2 max-[350px]:pb-1 max-[320px]:pt-1 max-[320px]:pb-0 max-w-7xl mx-auto sm:px-6 lg:px-8 max-[350px]:px-2 max-[320px]:px-1">
      <div className="flex w-80 max-[350px]:w-full max-[320px]:w-full mx-auto items-center mb-4 sm:mb-8 max-[375px]:mb-2 max-[350px]:mb-2 max-[320px]:mb-2 mt-0">
        <h1 className="text-xl max-[350px]:text-lg max-[320px]:text-lg grow font-bold dark:text-white">
          {WORDLE_TITLE}
        </h1>
        <SunIcon
          className="h-8 w-8 px-1 cursor-pointer dark:stroke-white"
          onClick={() => handleDarkMode(!isDarkMode)}
        />
        <InformationCircleIcon
          className="h-8 w-8 px-1 cursor-pointer dark:stroke-white"
          onClick={() => setIsInfoModalOpen(true)}
        />
        <ChartBarIcon
          className="h-8 w-8 px-1 cursor-pointer dark:stroke-white"
          onClick={() => setIsStatsModalOpen(true)}
        />
      </div>
      <div className="flex justify-center mb-2 max-[350px]:mb-2 max-[320px]:mb-2">
        <div
          className="text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 cursor-pointer bg-transparent border-none p-0"
        >
          Royn <a href="https://ordasnar.fo" target="_blank" rel="noopener noreferrer" className="text-red-600 dark:text-red-400 hover:text-red-700 underline dark:hover:text-red-300 cursor-pointer bg-transparent border-none p-0">orðasnar.fo</a>
        </div>
      </div>
      <Grid 
        guesses={guesses} 
        currentGuess={currentGuess} 
        cursorPosition={cursorPosition}
        onCellClick={onCellClick}
      />
      <Keyboard
        onChar={onChar}
        onDelete={onDelete}
        onEnter={onEnter}
        onArrowLeft={onArrowLeft}
        onArrowRight={onArrowRight}
        guesses={guesses}
      />
      <InfoModal
        isOpen={isInfoModalOpen}
        handleClose={() => setIsInfoModalOpen(false)}
        onAboutClick={() => setIsAboutModalOpen(true)}
        onWordsClick={() => setIsWordsModalOpen(true)}
      />
      <StatsModal
        isOpen={isStatsModalOpen}
        handleClose={() => setIsStatsModalOpen(false)}
        guesses={guesses}
        gameStats={stats}
        isGameLost={isGameLost}
        isGameWon={isGameWon}
        sessionId={sessionId}
        handleShare={() => {
          setSuccessAlert(GAME_COPIED_MESSAGE)
          return setTimeout(() => setSuccessAlert(''), ALERT_TIME_MS)
        }}
      />
      <AboutModal
        isOpen={isAboutModalOpen}
        handleClose={() => setIsAboutModalOpen(false)}
      />
      <WordsModal
        isOpen={isWordsModalOpen}
        handleClose={() => setIsWordsModalOpen(false)}
      />

      <Alert message={NOT_ENOUGH_LETTERS_MESSAGE} isOpen={isNotEnoughLetters} />
      <Alert
        message={WORD_NOT_FOUND_MESSAGE}
        isOpen={isWordNotFoundAlertOpen}
      />
      <Alert message={CORRECT_WORD_MESSAGE(solution)} isOpen={isGameLost} />
      <Alert
        message={successAlert}
        isOpen={successAlert !== ''}
        variant="success"
      />
    </div>
  )
}

export default App
